import { useEffect, useState } from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { AppShell, EmptyState, GpNav, Spinner, type NavItemSpec } from "@guestpilot/ui-kit";
import type { Permission } from "@guestpilot/contracts";
import {
  Building16Regular,
  CalendarCheckmark20Regular,
  ContactCard20Regular,
  Globe20Regular,
  Alert20Regular,
  Home20Regular,
  People20Regular,
  PeopleTeam20Regular,
  PersonDelete20Regular,
  ArrowExportUp20Regular,
  Settings20Regular,
  Payment20Regular,
  ShieldKeyhole20Regular,
} from "@fluentui/react-icons";
import { configureApi } from "./api/client";
import { helpUrl, supportMailto } from "./lib/helpLinks";
import { I18nProvider, useI18n, useT } from "./i18n";
import { AccountState, StateBanner } from "./pages/AccountState";
import { CampaignDetail, Campaigns } from "./pages/Campaigns";
import { Domains } from "./pages/Domains";
import { Exceptions } from "./pages/Exceptions";
import { Exports } from "./pages/Exports";
import { ExternalUsers } from "./pages/ExternalUsers";
import { Home } from "./pages/Home";
import { Notifications } from "./pages/Notifications";
import { useInbox } from "./lib/inbox";
import { ConsentHelp, Enrollment, Onboarding } from "./pages/Onboarding";
import { Removals } from "./pages/Removals";
import { Settings } from "./pages/Settings";
import { Subscription } from "./pages/Subscription";
import { TeamDetail } from "./pages/TeamDetail";
import { Teams } from "./pages/Teams";
import { TeamTab } from "./pages/team/TeamTab";
import { AuthEnd, AuthStart } from "./pages/AuthPopup";
import { PayPage } from "./pages/PayPage";
import { PublicSite } from "./pages/public/PublicSite";
import { SessionProvider, useSession, type Session } from "./session";
import type { TeamsInfo } from "./teams";

/** Which family of screens the account may reach (tenant lifecycle and suspension overlay, BR-016, BR-170, BR-252, 02 §19.2). */
export type Surface = "enroll" | "blocked" | "closed" | "app";
export function surfaceOf(
  s: Pick<Session, "tenantState"> & { me: { tenant: { suspension: string } } },
): Surface {
  if (s.me.tenant.suspension === "blocked") return "blocked";
  switch (s.tenantState) {
    case "pending_consent":
      return "enroll";
    case "archived":
    case "uninstalled":
    case "purged":
      return "closed";
    default:
      return "app"; // active, read_only, consent_lost, purge_scheduled keep the app with a banner
  }
}

/** Help and support in the left panel (BR-268); nothing is rendered until the public help site and the support address are configured. */
function HelpLinks() {
  const { t, locale } = useI18n();
  const help = helpUrl("setup", locale);
  const mail = supportMailto("GuestPilot");
  if (!help && !mail) return null;
  return (
    <span className="t-body-sm gp-nav-text">
      {help ? (
        <a href={help} target="_blank" rel="noopener noreferrer">
          {t("ui:nav.help")}
        </a>
      ) : null}
      {help && mail ? " · " : null}
      {mail ? <a href={mail}>{t("ui:nav.support")}</a> : null}
    </span>
  );
}

export function App({ teams }: { teams: TeamsInfo }) {
  return (
    <Routes>
      <Route path="/auth-start" element={<AuthStart />} />
      <Route path="/auth-end" element={<AuthEnd />} />
      <Route path="/pay" element={<PayPage />} />
      {/* outside Teams the address of the app is the public side: privacy, terms, help, support, Marketplace */}
      {teams.publicSite ? <Route path="/*" element={<PublicSite />} /> : null}
      <Route
        path="/*"
        element={
          <SessionProvider teams={teams}>
            {(state) => {
              if ("loading" in state) return <Splash />;
              if ("error" in state) return <Splash error />;
              return (
                <I18nProvider locale={state.session.me.locale}>
                  <LocaleSync locale={state.session.me.locale} />
                  <Gate teams={teams} />
                </I18nProvider>
              );
            }}
          </SessionProvider>
        }
      />
    </Routes>
  );
}

function LocaleSync({ locale }: { locale: string }) {
  useEffect(() => configureApi({ locale }), [locale]);
  return null;
}

function Splash({ error }: { error?: boolean }) {
  const t = useT();
  return (
    <div className="gp-state-page">
      {error ? <EmptyState title={t("errors:internal_error")} /> : <Spinner label={t("common:loading")} />}
    </div>
  );
}

function Gate({ teams }: { teams: TeamsInfo }) {
  const session = useSession();
  const surface = surfaceOf(session);
  const loc = useLocation();
  // a review notice opens the Team shell, even for an Admin who is Owner of the Team: they review as a Team Owner, not as an Admin
  const [reviewing, setReviewing] = useState(
    () => loc.pathname.startsWith("/review/") || Boolean(teams.subPageId?.startsWith("/review/")),
  );
  if (!reviewing && loc.pathname.startsWith("/review/")) setReviewing(true);
  if (surface === "enroll")
    return (
      <Routes>
        <Route path="/consent-help" element={<ConsentHelp />} />
        <Route path="*" element={<Enrollment />} />
      </Routes>
    );
  if (surface === "blocked") return <AccountState />;
  if (surface === "closed" && !teams.mobile)
    return (
      <Routes>
        <Route path="/subscription" element={<Chrome minimal />}>
          <Route index element={<Subscription />} />
        </Route>
        <Route path="*" element={<AccountState />} />
      </Routes>
    );
  if (teams.frame === "tab" || teams.frame === "config" || session.isTeamScopedOnly || reviewing)
    return <TeamTab teams={teams} key={loc.pathname.startsWith("/review") ? "r" : "t"} />;
  return (
    <>
      <OpenDeepLink page={teams.subPageId} />
      <Routes>
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/consent-help" element={<ConsentHelp />} />
        <Route element={<Chrome />}>
          <Route
            index
            element={
              <Guard perm="TEAM_READ">
                <Home />
              </Guard>
            }
          />
          <Route
            path="teams"
            element={
              <Guard perm="TEAM_READ">
                <Teams />
              </Guard>
            }
          />
          <Route
            path="teams/:teamId"
            element={
              <Guard perm="TEAM_READ">
                <TeamDetail />
              </Guard>
            }
          />
          <Route
            path="external-users"
            element={
              <Guard perm="PARTICIPANT_READ">
                <ExternalUsers />
              </Guard>
            }
          />
          <Route
            path="campaigns"
            element={
              <Guard perm="CAMPAIGN_READ">
                <Campaigns />
              </Guard>
            }
          />
          <Route
            path="campaigns/:campaignId"
            element={
              <Guard perm="CAMPAIGN_READ">
                <CampaignDetail />
              </Guard>
            }
          />
          <Route
            path="removals"
            element={
              <Guard perm="REMOVAL_READ">
                <Removals />
              </Guard>
            }
          />
          <Route
            path="exceptions"
            element={
              <Guard perm="EXCEPTION_READ">
                <Exceptions />
              </Guard>
            }
          />
          <Route
            path="domains"
            element={
              <Guard perm="DOMAIN_READ">
                <Domains />
              </Guard>
            }
          />
          <Route
            path="exports"
            element={
              <Guard perm="EXPORT_READ">
                <Exports />
              </Guard>
            }
          />
          <Route
            path="settings"
            element={
              <Guard perm="SETTINGS_READ">
                <Settings />
              </Guard>
            }
          />
          <Route
            path="subscription"
            element={
              <Guard perm="SUBSCRIPTION_READ">
                <Subscription />
              </Guard>
            }
          />
          <Route path="notifications" element={<Notifications />} />
          <Route path="account" element={<AccountState />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </>
  );
}

/** The personal app has a single Home tab: a notification links to it with the page to open (D-143), once at start. */
function OpenDeepLink({ page }: { page: string | undefined }) {
  const nav = useNavigate();
  useEffect(() => {
    if (page && /^\/[\w/-]*$/.test(page)) nav(page, { replace: true });
    // only at start: later navigation is the user's
  }, []);
  return null;
}

/** Route guard: a page the role may not open shows the forbidden page; the API still enforces everything (BR-007). */
export function Guard({ perm, children }: { perm: Permission; children: React.ReactNode }) {
  const s = useSession();
  const t = useT();
  // the subscription pages do not exist on Teams mobile: back to Home, not a dead end
  if (s.teams.mobile && perm.startsWith("SUBSCRIPTION_")) return <Navigate to="/" replace />;
  if (!s.can(perm)) return <EmptyState title={t("ui:forbidden")} />;
  return <>{children}</>;
}

const ICONS = {
  home: <Home20Regular />,
  notifications: <Alert20Regular />,
  teams: <PeopleTeam20Regular />,
  users: <People20Regular />,
  campaigns: <CalendarCheckmark20Regular />,
  removals: <PersonDelete20Regular />,
  exceptions: <ShieldKeyhole20Regular />,
  domains: <Globe20Regular />,
  exports: <ArrowExportUp20Regular />,
  settings: <Settings20Regular />,
  subscription: <Payment20Regular />,
  account: <ContactCard20Regular />,
};

/** Shell: GpNav full height beside a column of TopBar and content; items hidden for roles without access (04 AppShell). */
function Chrome({ minimal }: { minimal?: boolean }) {
  const t = useT();
  const s = useSession();
  const nav = useNavigate();
  const loc = useLocation();
  const inbox = useInbox();
  const unread = inbox.data?.unread ?? 0;
  const all: (NavItemSpec & { perm?: Permission })[] = [
    {
      path: "/",
      label: t("ui:nav.home"),
      icon: ICONS.home,
      section: t("ui:nav.workspace"),
      perm: "TEAM_READ",
    },
    {
      path: "/notifications",
      label: t("ui:nav.notifications"),
      icon: ICONS.notifications,
      section: t("ui:nav.workspace"),
      badge: unread,
      badgeLabel: t("ui:inbox.unreadLabel", { n: unread }),
    },
    {
      path: "/teams",
      label: t("ui:nav.teams"),
      icon: ICONS.teams,
      section: t("ui:nav.workspace"),
      perm: "TEAM_READ",
    },
    {
      path: "/external-users",
      label: t("ui:nav.users"),
      icon: ICONS.users,
      section: t("ui:nav.workspace"),
      perm: "PARTICIPANT_READ",
    },
    {
      path: "/campaigns",
      label: t("ui:nav.campaigns"),
      icon: ICONS.campaigns,
      section: t("ui:nav.workspace"),
      perm: "CAMPAIGN_READ",
    },
    {
      path: "/removals",
      label: t("ui:nav.removals"),
      icon: ICONS.removals,
      section: t("ui:nav.workspace"),
      perm: "REMOVAL_READ",
    },
    {
      path: "/exceptions",
      label: t("ui:nav.exceptions"),
      icon: ICONS.exceptions,
      section: t("ui:nav.workspace"),
      perm: "EXCEPTION_READ",
    },
    {
      path: "/domains",
      label: t("ui:nav.domains"),
      icon: ICONS.domains,
      section: t("ui:nav.workspace"),
      perm: "DOMAIN_READ",
    },
    {
      path: "/exports",
      label: t("ui:nav.exports"),
      icon: ICONS.exports,
      section: t("ui:nav.workspace"),
      perm: "EXPORT_READ",
    },
    {
      path: "/settings",
      label: t("ui:nav.settings"),
      icon: ICONS.settings,
      section: t("ui:nav.configure"),
      perm: "SETTINGS_READ",
    },
    {
      path: "/subscription",
      label: t("ui:nav.subscription"),
      icon: ICONS.subscription,
      section: t("ui:nav.configure"),
      perm: "SUBSCRIPTION_READ",
    },
  ];
  const items = all.filter((i) => (!i.perm || s.can(i.perm)) && (!minimal || i.path === "/subscription"));
  return (
    <AppShell
      nav={
        <GpNav
          label={t("ui:nav.label")}
          skipLabel={t("ui:nav.skip")}
          brand={t("common:appName")}
          items={items}
          active={loc.pathname}
          onNavigate={(p) => nav(p)}
          footer={
            <>
              <span className="t-body-strong gp-nav-text">
                <Building16Regular aria-hidden /> {s.me.tenant.name}
              </span>
              <span className="t-body-sm gp-nav-plan gp-nav-text">
                {s.me.tenant.state === "active"
                  ? t("ui:nav.planActive")
                  : t(`ui:nav.state.${s.me.tenant.state}`)}
              </span>
              <HelpLinks />
            </>
          }
        />
      }
    >
      <div className="gp-stack" style={{ gap: "var(--space-16)" }}>
        <StateBanner />
        <Outlet />
      </div>
    </AppShell>
  );
}
